import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './Localization';

const GameShowcase = ({ game }) => {
  const { t, language } = useLanguage(); // Use language context

  // Determine game name and alt text based on language
  const displayName = language === 'fr' && game.nameFr ? game.nameFr : game.name;
  const altText = displayName;

  return (
    <div className="group relative h-full bg-white rounded-3xl shadow-card hover:shadow-xl border border-gray-100 overflow-hidden transition-all duration-300">
      {/* Subtle gradient sheen on hover */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/0 to-brand-100/0 group-hover:to-brand-100/40 transition-colors duration-500" />

      {/* Game screenshot */}
      <div className="relative w-full px-4 pt-6 overflow-hidden">
        <img
          src={game.screenshots[0]}
          alt={altText}
          loading="lazy"
          className="w-full h-auto max-h-[300px] object-contain rounded-2xl transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>

      {/* Content */}
      <div className="relative p-6">
        <div className="flex items-center justify-center gap-3 mb-6">
          <img
            src={game.icon}
            alt={`${altText} icon`}
            loading="lazy"
            className="w-9 h-9 rounded-xl shadow-sm"
          />
          <h3 className="font-display text-2xl font-bold text-gray-900 text-center">
            {displayName}
          </h3>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-2 w-full px-2 sm:px-4">
          {game.stores.ios && (
            <a
              href={`https://apps.apple.com/app/id${game.stores.ios}`}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 bg-gray-900 text-white text-center px-3 sm:px-6 py-2.5 rounded-full hover:bg-black hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              {t('appStore')}
            </a>
          )}

          {game.stores.android && (
            <a
              href={`https://play.google.com/store/apps/details?id=${game.stores.android}`}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 bg-brand-600 text-white text-center px-3 sm:px-6 py-2.5 rounded-full hover:bg-brand-700 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              {t('googlePlay')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Container component for all the games
const GamesGrid = ({ games }) => {
  const { t } = useLanguage(); // Use language context

  return (
    <section id="games" className="py-24 bg-gradient-to-b from-slate-50 to-slate-100 scroll-mt-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="font-display text-4xl sm:text-5xl font-bold bg-gradient-to-r from-brand-600 to-emerald-500 bg-clip-text text-transparent">
            {t('ourGames')}
          </h2>
        </motion.div>

        <motion.div
          variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.05 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch"
        >
          {games.map((game) => (
            <motion.div
              key={game.id}
              variants={{
                hidden: { opacity: 0, y: 40 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
              }}
              className="w-full"
            >
              <GameShowcase game={game} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default GamesGrid;
